// Copyright (c) Docugami, Inc. All rights reserved.

import { DocumentCommandBar } from "components/file_management/presentation/DocumentCommandBar";
import { ListViewColumns } from "components/file_management/presentation/DocumentListView";
import { DocumentViewer, DocumentViewerProps, fileToViewItem, IDocumentViewControls, IDocumentViewItem } from "components/file_management/presentation/DocumentViewer";
import { getFileStatusInfo } from "filemanager/FileStatus";
import { useDownloadFilesCommand } from "hooks/DownloadFilesCommand";
import { useFilePreview } from "hooks/FilePreview";
import { useErrorFiles } from "hooks/FileViews";
import { useEffect, useMemo, useRef, useState } from "react";
import { getProviderIdFromPath } from "storage/StorageProviderHelpers";
import { useDeleteFilesCommand } from "../../hooks/DeleteFilesCommand";
import { DocumentCountInfo } from "./DocumentCountInfo";
import { InfoItemFooter } from "./ExplorerStyles";
import { ReviewDocsId } from "./ExplorerTypes";
import { ExplorerView } from "./ExplorerView";

export const ErrorFilesExplorerView = (): JSX.Element => {
    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    const viewRef = useRef<IDocumentViewControls>(null);
    const filter: ListViewColumns = ListViewColumns.Icon | ListViewColumns.Name | ListViewColumns.DocStatus | ListViewColumns.DocSource | ListViewColumns.AddedOn | ListViewColumns.Size;

    const { items } = useErrorFiles();
    const [documentIds, documentViewItems] = useMemo(() => {
        const ids: string[] = [];
        const viewItems: IDocumentViewItem[] = [];
        items.forEach(file => {
            if (!!file.properties.folderId) {
                ids.push(file.properties.folderId);
            }
            viewItems.push(fileToViewItem(file.properties, getFileStatusInfo(file.properties), undefined, getProviderIdFromPath(file.properties.path)))
        })
        return [ids, viewItems];
    }, [items]);

    const { filePreview, setPreviewFile, canPreview, previewCommands } = useFilePreview(documentIds);

    useEffect(() => {
        if (!filePreview) {
            viewRef.current?.selectItems(selectedItems);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [filePreview]);

    // Commands
    const downloadCommand = useDownloadFilesCommand(selectedItems, () => { });
    const deleteFilesCommand = useDeleteFilesCommand(selectedItems, () => { });
    const commands = useMemo(() => [downloadCommand, deleteFilesCommand], [downloadCommand, deleteFilesCommand]);

    const overlays = useMemo(() =>
        commands.filter(c => c.isActive && !!c.visual).map(x => x.visual),
        [commands]);

    const documentViewProps: DocumentViewerProps = {
        items: documentViewItems,
        selectedItems: selectedItems,
        filter: filter,
        onSelectedItemsChanged: items => setSelectedItems(items),
        actions: {
            name: item => setPreviewFile(item.folderId)
        },
        validateActions: {
            name: item => canPreview(item.folderId)
        }
    };

    const header = <DocumentCommandBar commands={!!filePreview ? previewCommands : commands} />
    const content = !!filePreview ? filePreview : <DocumentViewer ref={viewRef} {...documentViewProps} />
    const footer = (
        <InfoItemFooter>
            <DocumentCountInfo count={items.length} countName={'Document'} />
        </InfoItemFooter>
    )

    return (
        <ExplorerView
            header={header}
            content={content}
            overlays={!!filePreview ? previewCommands.map(x => x.visual) : overlays}
            footer={!!filePreview ? null : footer}
            telemetryId={ReviewDocsId.Error} />
    );
}
